import type {
  DC_Domain_Models_Products_CostDetailType,
  Products_Offers_ProductFutureCostDetail,
} from '@monorepo-types/dc';

/**
 * Pure netting arithmetic. No formatting, no copy and no knowledge of the offer shape live here, so every
 * figure the disclosure shows can be reasoned about (and tested) in isolation.
 */

/** The netting scheme (salderingsregeling) ends on this date; redelivery from it onward is no longer netted. */
export const NETTING_END_DATE = '2027-01-01';

export interface NettingPeriodYears {
  /** The last calendar year in which redelivery is netted. */
  current: number;
  /** The first calendar year without netting. */
  future: number;
}

/**
 * Period labels are anchored to the netting end date, not to today's date: a label computed from the current
 * year would silently shift by a year once 2027 begins.
 */
export const getNettingPeriodYears = (nettingEndDate: string = NETTING_END_DATE): NettingPeriodYears => {
  const future = new Date(nettingEndDate).getUTCFullYear();

  return { current: future - 1, future };
};

/**
 * Sums DC euro amounts, ignoring absent ones, so a section total always equals the rows it summarises.
 * Rounded to cents to keep floating-point noise out of the displayed total.
 */
export const sumAmounts = (...amounts: Array<number | undefined>): number =>
  Math.round(amounts.reduce<number>((total, amount) => total + (amount ?? 0), 0) * 100) / 100;

export interface PeriodSplit {
  /** The part that falls before {@link NETTING_END_DATE}, while netting still applies. */
  current: number;
  /** The part that falls on or after {@link NETTING_END_DATE}. */
  future: number;
}

// DC reports yearly kWh estimates; a missing, non-numeric or negative figure contributes nothing.
const toVolume = (value?: number | null): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;

/**
 * Splits a yearly volume into the part before and the part after the netting end date. The future part can
 * never exceed the total, so a DC figure that overshoots cannot produce a negative current period.
 */
export const splitByPeriod = (total?: number | null, futurePart?: number | null): PeriodSplit => {
  const totalVolume = toVolume(total);
  const future = Math.min(toVolume(futurePart), totalVolume);

  return { current: totalVolume - future, future };
};

/** The redelivered volume offset against consumption. Netting can never exceed what was consumed. */
export const getNettedVolume = (redelivery?: number | null, usage?: number | null): number =>
  Math.min(toVolume(redelivery), toVolume(usage));

/** Redelivery left over after netting: the volume the redelivery tariff is paid over. */
export const getRedeliveryRemainder = (redelivery?: number | null, usage?: number | null): number =>
  Math.max(0, toVolume(redelivery) - toVolume(usage));

/**
 * The disclosure is gated on this — not on `productFutureRate` existing. A future rate can be announced
 * without DC having broken the future period down into costs, and rendering on the rate alone produces a
 * disclosure with rates but no volumes or totals.
 */
export const hasFutureCostDetails = (
  futureCostDetails?: Products_Offers_ProductFutureCostDetail[] | null,
): futureCostDetails is Products_Offers_ProductFutureCostDetail[] => (futureCostDetails?.length ?? 0) > 0;

export const findFutureCostDetail = (
  futureCostDetails: Products_Offers_ProductFutureCostDetail[] | undefined,
  type: DC_Domain_Models_Products_CostDetailType,
): Products_Offers_ProductFutureCostDetail | undefined => futureCostDetails?.find(detail => detail.type === type);
